import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from '../../app/store';

export const sortVendors = (vendors: Vendor[]): Vendor[] => {
  return vendors.slice().sort((a, b) => a.vendorOrder - b.vendorOrder);
}

export const findVendorOrderNumber = (vendors: Vendor[], id: string): number => {
  return sortVendors(vendors).find(v => id === v.id)?.vendorOrder as number;
}

export const reduceOrderInList = (vendors: Vendor[], vendor: Vendor): { rise: Vendor, lower: Vendor } => {
  let risen: Vendor = {} as Vendor;
  let lowered: Vendor = {} as Vendor;
  const actionOrder = findVendorOrderNumber(vendors, vendor.id);
  if (actionOrder !== 0) {
    vendors.forEach(v => {
      const v_copy = structuredClone(v);
      if (v.vendorOrder === actionOrder - 1) {
        v_copy.vendorOrder = v_copy.vendorOrder + 1;
        risen = v_copy;
      } else if (v.vendorOrder === actionOrder) {
        v_copy.vendorOrder = v_copy.vendorOrder - 1;
        lowered = v_copy;
      }
    });
  }
  return { rise: risen, lower: lowered };
}

export const increaseOrderInList = (vendors: Vendor[], vendor: Vendor): { rise: Vendor, lower: Vendor } => {
  let risen: Vendor = {} as Vendor;
  let lowered: Vendor = {} as Vendor;
  const actionOrder = findVendorOrderNumber(vendors, vendor.id);
  if (actionOrder !== vendors.length - 1) {
    vendors.forEach(v => {
      const v_copy = structuredClone(v);
      if (v.vendorOrder === actionOrder + 1) {
        v_copy.vendorOrder = v_copy.vendorOrder - 1;
        lowered = v_copy;
      } else if (v.vendorOrder === actionOrder) {
        v_copy.vendorOrder = v_copy.vendorOrder + 1;
        risen = v_copy;
      }
    });
  }
  return { rise: risen, lower: lowered };
}

export interface Vendor {
  id: string,
  author: string,
  created: string,
  updated: string,
  vendorOrder: number,
  name: string,
  description: string,
  website: string,
  category: string,
  imageUrl: string,
}

export interface VendorState {
  vendor: Vendor,
  vendors: Vendor[],
  vendorOrderValue: number,
  status: 'idle' | 'pending' | 'succeeded' | 'failed',
  error: null | string
}

export interface VendorApiPayload {
  accessToken: string,
  id: null | string,
  vendor: null | Vendor,
}

const initialState: VendorState = {
  vendor: null,
  vendors: [],
  vendorOrderValue: 0,
  status: 'idle',
  error: null
} as unknown as VendorState;

export const fetchVendors = createAsyncThunk<Vendor[], void, { readonly rejectValue: { readonly errorMessage: string } }>(
  'vendors/fetch',
  async (_, thunkAPI) => {
    return await fetch('/api/vendors/', { method: 'GET' })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error(`Unexpected response from server (code ${response.status}).`);
        }
      })
      .catch((error) => {
        console.error(error);
        return thunkAPI.rejectWithValue({ errorMessage: error.message });
      });
  }
);

export const fetchVendor = createAsyncThunk<Vendor, VendorApiPayload, { readonly rejectValue: { readonly errorMessage: string } }>(
  'vendor/fetch',
  async (arg, thunkAPI) => {
    return await fetch(`/api/vendors/${arg.id}`, { method: 'GET' })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error(`Unexpected response from server (code ${response.status}).`);
        }
      })
      .catch((error) => {
        console.error(error);
        return thunkAPI.rejectWithValue({ errorMessage: error.message });
      });
  }
);

export const createVendor = createAsyncThunk<Vendor, VendorApiPayload, { rejectValue: { readonly errorMessage: string, readonly vendor: VendorApiPayload } }>(
  'vendor/create',
  async (arg, thunkAPI) => {
    if (!arg.vendor) {
      throw new Error(`No vendor in request.`);
    }
    return await fetch('/api/vendors/', {
      method: 'POST',
      body: JSON.stringify(arg.vendor),
      ...(arg.accessToken ? { headers: { Authorization: `Basic ${arg.accessToken}` } } : {})
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error(`Unexpected response from server (code ${response.status}).`);
        }
      })
      .catch((error) => {
        console.error(error);
        return thunkAPI.rejectWithValue({ errorMessage: error.message, vendor: arg });
      });
  }
);

export const editVendor = createAsyncThunk<Vendor, VendorApiPayload, { rejectValue: { readonly errorMessage: string, readonly vendorApiPayload: VendorApiPayload } }>(
  'vendor/edit',
  async (arg, thunkAPI) => {
    if (!arg.vendor) {
      throw new Error(`No vendor in request.`);
    }
    return await fetch('/api/vendors/', {
      method: 'PATCH',
      body: JSON.stringify(arg.vendor),
      ...(arg.accessToken ? { headers: { Authorization: `Basic ${arg.accessToken}` } } : {})
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error(`Unexpected response from server (code ${response.status}).`);
        }
      })
      .catch((error) => {
        console.error(error);
        return thunkAPI.rejectWithValue({ errorMessage: error.message, vendorApiPayload: arg });
      });
  }
);

export const deleteVendor = createAsyncThunk<string, VendorApiPayload, { rejectValue: { readonly errorMessage: string } }>(
  'vendor/delete',
  async (arg, thunkAPI) => {
    return await fetch(`/api/vendors/${arg.id}`, {
      method: 'DELETE',
      body: JSON.stringify(arg.vendor),
      ...(arg.accessToken ? { headers: { Authorization: `Basic ${arg.accessToken}` } } : {})
    })
      .then(response => {
        if (response.status === 200) {
          return '' + response.body;
        } else {
          throw new Error(`Unexpected response from server (code ${response.status}).`);
        }
      })
      .catch((error) => {
        console.error(error);
        return thunkAPI.rejectWithValue({ errorMessage: error.message });
      });
  }
);

export const vendorsSlice = createSlice({
  name: 'vendorsstate',
  initialState,
  reducers: {
    reset: () => initialState,
    setStatus: (state, action: { type: string, payload: 'idle' | 'pending' | 'succeeded' | 'failed' }) => {
      state.status = action.payload;
    },
    setVendorOrderValue: (state, action: { type: string, payload: number }) => {
      state.vendorOrderValue = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendors.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.vendors = action.payload.sort((a, b) => a.vendorOrder - b.vendorOrder);
        state.error = '';
        state.status = 'succeeded';
      })
      .addCase(fetchVendors.rejected, (state) => {
        state.status = 'failed';
      })

      .addCase(fetchVendor.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchVendor.fulfilled, (state, action) => {
        state.vendor = action.payload;
        state.error = '';
        state.status = 'succeeded';
      })
      .addCase(fetchVendor.rejected, (state, action) => {
        if (action.payload !== undefined) {
          state.error = action.payload.errorMessage;
        }
        state.status = 'failed';
      })

      .addCase(createVendor.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(createVendor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(createVendor.rejected, (state, action) => {
        if (action.payload !== undefined) {
          state.error = action.payload.errorMessage;
        }
        state.status = 'failed';
      })

      .addCase(editVendor.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(editVendor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(editVendor.rejected, (state, action) => {
        if (action.payload !== undefined) {
          state.error = action.payload.errorMessage;
        }
        state.status = 'failed';
      })

      .addCase(deleteVendor.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(deleteVendor.fulfilled, (state, action) => {
        state.vendor = {} as Vendor;
        if (state.vendors.length > 0) {
          state.vendors.splice(state.vendors.findIndex(v => v.id === action.payload), 1)
        }
        state.error = '';
        state.status = 'succeeded';
      })
      .addCase(deleteVendor.rejected, (state, action) => {
        if (action.payload !== undefined) {
          state.error = action.payload.errorMessage;
        }
        state.status = 'failed';
      });
  },
});

export const { reset, setVendorOrderValue } = vendorsSlice.actions;

export const selectAllVendors = (state: RootState) => sortVendors(state.vendorsstate.vendors);
export const selectVendorById = (state: RootState, vendorId: string) =>
  state.vendorsstate.vendors.find(v => v.id === vendorId);
export const selectVendorStatus = (state: RootState) => state.vendorsstate.status;
export const selectVendorError = (state: RootState) => state.vendorsstate.error;

export default vendorsSlice.reducer;
